import { Box, ThemeSpaceVariable, MoreHorizontalIcon } from "@caspeco/casper-ui-library";
import { type JSX, useEffect, useRef, useState } from "react";
import ReloadContainer from "./reload-container";
import { useAppContext } from "../hooks/use-app-context";
import { PageHeader } from "./page-header";
import "./page.css";
import { EnvironmentHeader } from "./environmen-header";

type reloadContainerPageProps =
	| {
			reload?: undefined; // If reload is not provided, loadingState is not required
			reloadLoadingState?: never;
			onScrollBorttom?: never;
	  }
	| {
			/** Adds a reload container wrapping the page. If reload is provided, loadingState must be provided aswell */
			reload: () => void;
			reloadLoadingState: boolean;
			onScrollBorttom?: (event: React.UIEvent<HTMLDivElement>) => void;
	  };

type pageProps = {
	children: JSX.Element;
	title: string;
	/** Title should be a subheader and centered */
	subheader?: boolean;
	/** Adds extra button to the page */
	headerButtons?: JSX.Element;
	/** Shows page filter button */
	showFilter?: boolean;
	/** Add a element for more button located right in the header*/
	moreButton?: JSX.Element;
	/** Add a element for add button located next to title in the header */
	addButton?: JSX.Element;
	/** Add extra content to header */
	headerContent?: JSX.Element;
	collapsableHeader?: boolean;
	onHeaderHeightChange?: (height: number) => void;
} & reloadContainerPageProps;

const Page = (props: pageProps) => {
	const [headerHeight, setHeaderHeight] = useState<number>(0);
	const [headerContentHeight, setHeaderContentHeight] = useState<number>();
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [isRecalculatingHeight, setIsRecalculatingHeight] = useState<boolean>(false);
	const { safeArea } = useAppContext();

	// Ref to measure the header height
	const headerRef = useRef<HTMLDivElement>(null);
	const headerContentRef = useRef<HTMLDivElement>(null);
	const collapsableRef = useRef<HTMLDivElement>(null);
	const transitionBoxRef = useRef<HTMLDivElement>(null);
	// Refs for dragging
	const startY = useRef(0);
	const startHeight = useRef(0);
	const MIN_HEIGHT = useRef(0);
	const MAX_HEIGHT = useRef(0);

	// Observe header height changes
	const resizeObserver = useRef(
		new ResizeObserver((_) => {
			const height = headerRef.current?.offsetHeight;
			if (!isDragging && height) {
				setHeaderHeight(height);
				if (props.onHeaderHeightChange) {
					props.onHeaderHeightChange(height);
				}
			}
		}),
	);

	if (headerRef.current) {
		resizeObserver.current.observe(headerRef.current);
	}

	// Observe header content height changes
	const resizeContentObserver = useRef(
		new ResizeObserver((_) => {
			if (!isDragging && headerContentRef.current) {
				if (MAX_HEIGHT.current !== headerContentRef.current.offsetHeight) {
					setHeaderContentHeight(headerContentRef.current.offsetHeight);
					MAX_HEIGHT.current = headerContentRef.current.offsetHeight;

					setIsRecalculatingHeight(true);
				}
			}
		}),
	);

	if (headerContentRef.current) {
		resizeContentObserver.current.observe(headerContentRef.current);
	}

	// Set initial header content height
	useEffect(() => {
		if (headerRef.current) {
			setHeaderContentHeight(headerRef.current.offsetHeight);
		}
		if (headerContentRef.current) {
			setHeaderContentHeight(headerContentRef.current.offsetHeight);
		}
	}, []);

	useEffect(() => {
		if (transitionBoxRef.current) {
			transitionBoxRef.current.style.transition =
				isDragging || isRecalculatingHeight ? "none" : "height 0.25s ease";
		}
	}, [isDragging, isRecalculatingHeight]);

	const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		startY.current = e.clientY;
		startHeight.current = headerContentHeight!;
		setIsDragging(true);
		setIsRecalculatingHeight(false);
	};

	const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		if (!isDragging) return;

		const delta = e.clientY - startY.current;
		let newHeight = startHeight.current + delta;

		newHeight = Math.max(MIN_HEIGHT.current, Math.min(MAX_HEIGHT.current, newHeight));
		setHeaderContentHeight(newHeight);
	};

	const onPointerUp = () => {
		if (!isDragging) return;

		setIsDragging(false);

		const snapPoint = 60;

		if (startHeight.current === 0) {
			if (headerContentHeight! > snapPoint) {
				setHeaderContentHeight(MAX_HEIGHT.current);
			} else {
				setHeaderContentHeight(MIN_HEIGHT.current);
			}
		} else {
			if (headerContentHeight! < MAX_HEIGHT.current - snapPoint) {
				setHeaderContentHeight(MIN_HEIGHT.current);
			} else {
				setHeaderContentHeight(MAX_HEIGHT.current);
			}
		}
	};

	return (
		<Box
			className="page"
			overflowY={!props.reload ? "scroll" : "unset"}
			overflowX={"hidden"}
			position="relative"
			display="flex"
			flexDirection="column"
			height={`calc(100dvh - 74px - ${safeArea.bottom}px)`}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
		>
			<Box
				id="page-header"
				className="page-header"
				ref={headerRef}
				paddingTop={safeArea.topAsPx()}
				position="fixed"
				w={"100%"}
				zIndex={2}
			>
				<EnvironmentHeader />
				<PageHeader
					title={props.title}
					addButton={props.addButton}
					headerButtons={props.headerButtons}
					showFilter={props.showFilter}
					moreButton={props.moreButton}
					subheader={props.subheader}
				/>
				{props.headerContent && (
					<>
						<Box ref={transitionBoxRef} h={headerContentHeight + "px"} overflow={"hidden"}>
							<Box
								mx={ThemeSpaceVariable.Medium}
								pb={!props.collapsableHeader ? ThemeSpaceVariable.Small : undefined}
								ref={headerContentRef}
							>
								{props.headerContent}
							</Box>
						</Box>
						{props.collapsableHeader && (
							<Box
								ref={collapsableRef}
								display={"flex"}
								onPointerDown={onPointerDown}
								userSelect={"none"}
								w={"100%"}
								justifyContent={"center"}
								className="page-collapsible-header"
							>
								<MoreHorizontalIcon w={"100%"} />
							</Box>
						)}
					</>
				)}
			</Box>
			<ReloadContainer
				loadingState={props.reloadLoadingState}
				reload={props.reload}
				onScrollBorttom={props.onScrollBorttom}
			>
				<Box px={ThemeSpaceVariable.Medium} pt={`${headerHeight}px`} height={"100%"}>
					{props.children}
				</Box>
			</ReloadContainer>
		</Box>
	);
};

export default Page;
